import { inject, Injectable } from '@angular/core';
import { VerifyOTP } from '@models/auth.model';
import { ISyspolMenu, ISubMenu } from '@models/modules.model';
import { Position } from '@models/userDTO.model';
import { REPUVE_POSITIONS_ENUM } from '@shared/data/data';
import { MODULES_ENUM } from '@shared/data/modules';
import { DOMAIN_MENU } from '@shared/data/modules-superadmin';
import { SUBDOMAIN_MENU } from '@shared/data/modules-syspol';
import { SUBDOMAINS } from '@shared/data/subdomains.';
import { SessionService } from './session';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  /* SERVICES */
  readonly #sessionService = inject(SessionService);
  readonly #stateService = inject(StateService);
  /* SERVICES */

  private validSubdomains = SUBDOMAINS;
  private menuDomain: ISyspolMenu[] = DOMAIN_MENU;
  private menuSubdomain: ISyspolMenu[] = SUBDOMAIN_MENU;

  public currentModules: number[] = [];
  public currentLevel: number;
  public currentPositions: number[] = [];
  public currentPositionRepuve: REPUVE_POSITIONS_ENUM | null = null;
  public menuWithPermissions: ISyspolMenu[] = [];
  public uuid: string | undefined;

  /**
  * @method setModulesPermissions
  * @description Establece asincrónicamente los permisos del módulo basándose en la URL actual y el rol del usuario.
  * @description Recupera el rol del usuario y los módulos asociados si no están disponibles.
  * @description Filtra el menú según los permisos del usuario.
  */
  public async setModulesPermissions(idMenu: number): Promise<ISyspolMenu[]> {
    const host = window.location.hostname;
    const parts = host.split('.');
    const subdomain = parts.length > 1 ? parts[0] : undefined;
    localStorage.setItem('subdomain', JSON.stringify(subdomain));

    const isValidSubdomain = this.validSubdomains.includes(subdomain ?? '');

    if (isValidSubdomain) {
      //* Se recargan los módulos cuando cambia el usuario en sesión (logout/login sin recargar la pestaña).
      const uuid = this.#sessionService.currentUser()?.uuid;
      if (!uuid) {
        this.uuid = undefined;
        this.currentModules = [];
      } else if (uuid !== this.uuid) {
        this.uuid = uuid;
        const data = await this.#stateService.getUserPermissionsByUUIDSignal(uuid);
        this.currentModules = data()?.map(module => module.module_id) ?? [];
      }
    } else {
      const dataAux: any = localStorage.getItem('data');
      const data: VerifyOTP = JSON.parse(dataAux);
      if (data) {
        this.currentLevel = data.positions[0].level;
        this.currentPositions = data.positions.map((position: Position) => position.position_id);
        this.currentPositionRepuve = data.position_repuve;
      }
    }
    return this.filterMenu(idMenu, isValidSubdomain);
  }

  /**
  * @method filterMenu
  * @description Filtra el menú para mostrar solo elementos accesibles para el usuario actual.
  * @description Modifica el menú y las propiedades de menuWithPermissions en su lugar.
  */
  private filterMenu(idMenu: number, isValidSubdomain: boolean): ISyspolMenu[] {
    this.menuWithPermissions = [];

    if (isValidSubdomain) {
      for (const menu of this.menuSubdomain) {
        const modTemp: ISyspolMenu = { ...menu };

        if (menu.idMenu.includes(idMenu)) modTemp.open = true;

        if (menu.subMenu.length) {
          modTemp.subMenu = menu.subMenu.filter((subMenu: ISubMenu) => this.currentModules.includes(subMenu.idModules));
          if (modTemp.subMenu.length) this.menuWithPermissions.push(modTemp);
        } else {
          if (this.currentModules.includes(menu.idModules[0]) || menu.idModules[0] === 1) {
            this.menuWithPermissions.push(modTemp);
          }
        }
      }
    } else {
      for (const menu of this.menuDomain) {
        if (menu?.levels && menu.levels.includes(this.currentLevel)) {
          const [idModule] = menu.idModules;
          const commonModules = [MODULES_ENUM.PROFILE, MODULES_ENUM.EVENTS];

          //* Copia para no mutar la constante DOMAIN_MENU.
          const modTemp: ISyspolMenu = { ...menu, open: menu.idMenu.includes(idMenu) };

          if ((this.currentPositionRepuve && menu.repuvePositions?.length) && !menu.repuvePositions?.includes(this.currentPositionRepuve)) continue;
          if (this.currentModules.includes(idModule) || commonModules.includes(idModule)) {
            this.menuWithPermissions.push(modTemp);
          } else {
            if (menu.positionsId?.some((position: number) => this.currentPositions.includes(position))) this.menuWithPermissions.push(modTemp);
          }
        }
      }
    }
    return this.menuWithPermissions;
  }
}
